import React, { useEffect, useRef, useState } from "react";
import Moveable from "react-moveable";
import { toast } from "../../helpers/swal";
import { settings } from "../../helpers/settings";
import axios from "axios";
import { Link } from "react-router-dom";
const areaTypes = [
  "Full Front",
  "Full Back",
  "Pocket",
  "Left Arm",
  "Right Arm",
];

const MockupEditorPage = () => {
  const [mockups, setMockups] = useState([]);
  const [mockupName, setMockupName] = useState("");
  const [productType, setProductType] = useState("");
  const [color, setColor] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [areas, setAreas] = useState([]);
  const [availableAreas, setAvailableAreas] = useState(areaTypes);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedMockupId, setSelectedMockupId] = useState(null);
  const [originalImageSize, setOriginalImageSize] = useState({ width: 1, height: 1 });
  const canvasRef = useRef(null);

  useEffect(() => {
    fetchMockups();
  }, []);

  const fetchMockups = async () => {
    try {
      const response = await axios.get(`${settings.apiURL}/get_mockups.php`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
        },
      });
      if (response.data.success && Array.isArray(response.data.mockups)) {
        setMockups(response.data.mockups);
      }
    } catch (err) {
      console.log("Mockup fetch failed", err);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setImage(file);
      setImagePreview(url);

      const img = new Image();
      img.onload = () => {
        setOriginalImageSize({ width: img.width, height: img.height }
          
        );
        
      };
      img.src = url;
    }
  };

  const addArea = (type) => {
    const area = {
      name: type,
      x: 50,
      y: 50,
      width: 150,
      height: 150,
      rotate: 0,
    };
    setAreas((prev) => [...prev, area]);
    setAvailableAreas((prev) => prev.filter((a) => a !== type));
  };

const loadMockup = (mockup) => {
  setSelectedMockupId(mockup.id);
  setMockupName(mockup.mockup_name);
  setProductType(mockup.product_type);
  setColor(mockup.color || "");
  setImage(null);
  setImagePreview(`${settings.apiURL}/${mockup.image_path}`);

  let parsed = {};
  try {
    parsed = JSON.parse(mockup.positions || "{}");
  } catch (e) {
    console.error("Invalid positions JSON:", e);
    toast("Failed to load mockup positions.");
    return;
  }

  const img = new Image();
  img.onload = () => {
    const width = img.width;
    const height = img.height;
    setOriginalImageSize({ width, height });

    if (!parsed || Object.keys(parsed).length === 0) {
      toast("No design areas found in this mockup.");
      setAreas([]);
      setAvailableAreas(areaTypes);
      return;
    }

    const newAreas = Object.entries(parsed).map(([name, pos]) => ({
      name,
      x: pos.x * width,
      y: pos.y * height,
      width: pos.width * width,
      height: pos.height * height,
      rotate: pos.rotate || 0,
    }));

    setAreas(newAreas);
    setAvailableAreas(
      areaTypes.filter((area) => !Object.keys(parsed).includes(area))
    );
  };

  img.onerror = () => {
    toast("Failed to load mockup image.");
  };

  img.src = `${settings.apiURL}/${mockup.image_path}`;
};


  const validateInputs = () => {
    if (!mockupName || !productType || !color || (!imagePreview && !image)) {
      toast("Please fill in all required fields.");
      return false;
    }
    if (areas.length === 0) {
      toast("Please place at least one design area.");
      return false;
    }
    return true;
  };

  const saveMockup = async () => {
    if (!validateInputs()) return;

    const exists = mockups.some(
      (m) =>
        m.mockup_name.toLowerCase() === mockupName.toLowerCase() &&
        m.product_type.toLowerCase() === productType.toLowerCase() &&
        m.color.toLowerCase() === color.toLowerCase() &&
        m.id !== selectedMockupId
    );
    if (exists) {
      toast("A mockup with the same details already exists.");
      return;
    }

    const formData = new FormData();

    const normalizedAreas = areas.map((area) => ({
      name: area.name,
      x: area.x / originalImageSize.width,
      y: area.y / originalImageSize.height,
      width: area.width / originalImageSize.width,
      height: area.height / originalImageSize.height,
      rotate: area.rotate || 0,
    }));

    formData.append("mockup_name", mockupName);
    formData.append("product_type", productType);
    formData.append("color", color);
    formData.append("areas", JSON.stringify(normalizedAreas));
    if (image) formData.append("image", image);
    if (selectedMockupId) formData.append("id", selectedMockupId);

    const endpoint = selectedMockupId
      ? `${settings.apiURL}/update_mockup.php`
      : `${settings.apiURL}/save_mockup.php`;

    setIsSaving(true);
    try {
      const response = await axios.post(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
        },
      });

      if (response.data.success) {
        toast("Mockup saved successfully.");
        resetForm();
        fetchMockups();
      } else {
        toast(response.data.message || "Saving failed.");
      }
    } catch (err) {
      toast("Saving failed.");
    } finally {
      setIsSaving(false);
    }
  };

  const deleteMockup = async () => {
    if (!selectedMockupId) return;
    try {
      const response = await axios.post(
        `${settings.apiURL}/delete_mockup.php`,
        {
          id: selectedMockupId,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
          },
        }
      );
      if (response.data.success) {
        toast("Mockup deleted.");
        resetForm();
        fetchMockups();
      } else {
        toast("Delete failed.");
      }
    } catch (err) {
      toast("Delete failed.");
    }
  };

  const resetForm = () => {
    setSelectedMockupId(null);
    setMockupName("");
    setProductType("");
    setColor("");
    setImage(null);
    setImagePreview(null);
    setAreas([]);
    setAvailableAreas(areaTypes);
  };

  const duplicateMockup = () => {
    setSelectedMockupId(null);
    toast("Mockup duplicated. Make changes and click Save.");
  };




  return (
    <>
    <div className="d-flex justify-content-center mt-3 mb-2">
      <Link to="/home" className="btn"     style={{
          backgroundColor: "#ff9800",
          color: "white",
          fontSize: 16,
          fontWeight: "bold",
          padding: "8px 16px",
          border: "none",
          borderRadius: 4,
          textDecoration: "none",
        }}>
        🏠 Home
      </Link>
    </div>
    
    <div style={{ display: "flex", padding: 20, fontFamily: "Arial" }}>
      

      <div
        style={{
          width: 200,
          marginRight: 30,
          height: "80vh",
          overflowY: "auto",
          border: "1px solid #ccc",
          paddingRight: 10,
        }}
      >
        <h3>Saved Mockups</h3>
        {mockups.length === 0 && <p>No mockups saved yet.</p>}
        <ul style={{ padding: 0, listStyle: "none" }}>
          {mockups.map((m) => (
            <li
              key={m.id}
              onClick={() => loadMockup(m)}
              style={{ cursor: "pointer", marginBottom: 15 }}
            >
              <img
                src={`${settings.apiURL}/${m.image_path}`}
                alt={m.mockup_name}
                width="100"
                style={{ display: "block", marginBottom: 5 }}
              />
              <strong>{m.mockup_name}</strong>
            </li>
          ))}
        </ul>
      </div>

      <div style={{ flex: 1 }}>
        <h2>{selectedMockupId ? "Edit Mockup" : "Create New Mockup"}</h2>
        {selectedMockupId && (
          <button
            onClick={resetForm}
            style={{
              marginBottom: 10,
              padding: "6px 12px",
              background: "#1976d2",
              color: "white",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
            }}
          >
            Back to Create
          </button>
        )}

        <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
          <input
            placeholder="Mockup Name"
            value={mockupName}
            onChange={(e) => setMockupName(e.target.value)}
            style={{ padding: 8, fontSize: 14, width: 150 }}
          />
          <input
            placeholder="Product Type"
            value={productType}
            onChange={(e) => setProductType(e.target.value)}
            style={{ padding: 8, fontSize: 14, width: 150 }}
          />
          <input
            placeholder="Color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            style={{ padding: 8, fontSize: 14, width: 100 }}
          />
          <input type="file" onChange={handleImageChange} />
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 10,
          }}
        >
          {availableAreas.map((area, index) => (
            <button
              key={index}
              onClick={() => addArea(area)}
              style={{
                backgroundColor: "#ff9800",
                color: "white",
                padding: "6px 10px",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
              }}
            >
              {area}
            </button>
          ))}
        </div>

        {imagePreview && (
          <div
            style={{
              position: "relative",
              display: "inline-block",
              boxShadow: "0 0 10px rgba(0,0,0,0.1)",
              padding: 10,
              background: "#fff",
            }}
          >
            <div style={{ position: "relative", width: 700 }}>
             <img src={imagePreview} alt="Mockup" style={{ width: "512px", height: "auto" }} />

              {areas.map((area, index) => (
                <Moveable
                  key={index}
                  target={() => document.querySelector(`#area-${index}`)}
                  draggable
                  resizable
                  rotatable
                  throttleResize={1}
                  throttleRotate={1}
                  renderDirections={["nw", "ne", "sw", "se"]}
                  onDrag={({ target, left, top }) => {
                    target.style.left = `${left}px`;
                    target.style.top = `${top}px`;
                    const newAreas = [...areas];
                    newAreas[index].x = left;
                    newAreas[index].y = top;
                    setAreas(newAreas);
                  }}
                  onResize={({ target, width, height, drag }) => {
                    target.style.width = `${width}px`;
                    target.style.height = `${height}px`;
                    target.style.left = `${drag.left}px`;
                    target.style.top = `${drag.top}px`;
                    const newAreas = [...areas];
                    newAreas[index].x = drag.left;
                    newAreas[index].y = drag.top;
                    newAreas[index].width = width;
                    newAreas[index].height = height;
                    setAreas(newAreas);
                  }}
                  onRotate={({ target, beforeRotate }) => {
                    target.style.transform = `rotate(${beforeRotate}deg)`;
                    const newAreas = [...areas];
                    newAreas[index].rotate = beforeRotate;
                    setAreas(newAreas);
                  }}
                />
              ))}
              {areas.map((area, index) => (
                <div
                  key={`box-${index}`}
                  id={`area-${index}`}
                  style={{
                    position: "absolute",
                    top: area.y,
                    left: area.x,
                    width: area.width,
                    height: area.height,
                    transform: `rotate(${area.rotate || 0}deg)`,
                    background: "rgba(255,0,0,0.05)",
                    border: "2px dashed red",
                    color: "red",
                    fontWeight: "bold",
                    textAlign: "center",
                    fontSize: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    userSelect: "none",
                  }}
                >
                  {area.name}
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: 20, display: "flex", gap: 10 }}>
          <button
            onClick={saveMockup}
            disabled={isSaving}
            style={{
              padding: "10px 20px",
              background: "#ff9800",
              color: "white",
              border: "none",
              borderRadius: 4,
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            {isSaving ? "Saving..." : selectedMockupId ? "Update" : "Save"}
          </button>
          {selectedMockupId && (
            <button
              onClick={duplicateMockup}
              style={{
                padding: "10px 20px",
                background: "#2196f3",
                color: "white",
                border: "none",
                borderRadius: 4,
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              Duplicate
            </button>
          )}

          {selectedMockupId && (
            <button
              onClick={deleteMockup}
              style={{
                padding: "10px 20px",
                background: "#f44336",
                color: "white",
                border: "none",
                borderRadius: 4,
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default MockupEditorPage;
